import axios from "axios";
import { getToken } from "./userService";

const API_BASE_URL = "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Get comprehensive dashboard data for a user
 * @param {number} userId - The user ID
 * @returns {Promise<Object|null>} Dashboard data or null if error
 */
export async function getUserDashboardData(userId) {
  try {
    const response = await axios.get(`${API_BASE_URL}/user/dashboard/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    
    return response.data;
  } catch (error) {
    // console.error("Error fetching dashboard data:", error);
    return null;
  }
}

/**
 * Get user's games list
 * @param {number} userId - The user ID
 * @returns {Promise<Array|null>} Array of user games or null if error
 */
export async function getUserGames(userId) {
  try {
    const response = await axios.get(`${API_BASE_URL}/user/games/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    // console.error("Error fetching user games:", error);
    return [];
  }
}

/**
 * Get user's activity history
 * @param {number} userId - The user ID
 * @param {number} limit - Number of activities to fetch (optional)
 * @returns {Promise<Array|null>} Array of user activities or null if error
 */
export async function getUserActivities(userId) {
  try {
    const response = await axios.get(`${API_BASE_URL}/user/activities/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    // console.error("Error fetching user activities:", error);
    return [];
  }
}

/**
 * Get user's tasks
 * @param {number} userId - The user ID
 * @returns {Promise<Array|null>} Array of user tasks or null if error
 */
export async function getUserTasks(userId) {
  try {
    const response = await axios.get(`${API_BASE_URL}/user/tasks/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    // console.error("Error fetching user tasks:", error);
    return [];
  }
}

/**
 * Get leaderboard data
 * @param {number} limit - Number of top players to fetch (optional)
 * @returns {Promise<Object|null>} Leaderboard data or null if error
 */
export async function getLeaderboard() {
  try {
    const response = await axios.get(`${API_BASE_URL}/leaderboard`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
      params: { limit: 10 },
    });
    return response.data;
  } catch (error) {
    // console.error("Error fetching leaderboard:", error);
    return { leaderboard: [], currentUserRank: null, totalUsers: 0 };
  }
}

/**
 * Update user statistics
 * @param {number} userId - The user ID
 * @param {Object} statsData - Statistics data to update
 * @returns {Promise<Object|null>} Updated user data or null if error
 */
export async function updateUserStats(userId, statsData) {
  try {
    const response = await axios.put(`${API_BASE_URL}/user/stats/${userId}`, statsData, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    // console.error("Error updating user stats:", error);
    return null;
  }
}

/**
 * Get user ID from stored user data or JWT token
 * @returns {number|null} User ID or null if not found
 */
export function getCurrentUserId() {
  // First, try to get user ID from localStorage
  const userId = localStorage.getItem('userId');
  if (userId) {
    return parseInt(userId);
  }

  // Try to get user ID from stored user data
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      const parsed = JSON.parse(userData);
      return parsed.id || parsed._id;
    } catch (error) {
      // console.error("Error parsing user data:", error);
    }
  }

  // Try to decode JWT token to get user ID
  const token = localStorage.getItem('token');
  if (token) {
    try {
      // Decode JWT token (basic decode, not verification)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const decoded = JSON.parse(jsonPayload);
      const extractedUserId = decoded.userId || decoded.id || decoded.sub;
      
      if (extractedUserId) {
        // Store for future use
        setCurrentUserId(extractedUserId);
        return parseInt(extractedUserId);
      }
    } catch (error) {
      // console.error("Error decoding JWT token:", error);
    }
  }

  // If we still don't have a user ID, we need to fetch it from the backend
  // using the current authentication
  return null;
}

/**
 * Store user ID for future use
 * @param {number} userId - The user ID to store
 */
export function setCurrentUserId(userId) {
  localStorage.setItem('userId', userId.toString());
}

/**
 * Get current user profile from backend
 * This will help us get the user ID and updated profile data
 * @returns {Promise<Object|null>} User profile data or null if error
 */
export async function getCurrentUserProfile() {
  try {
    const endpoint = `${API_BASE_URL}/profile`;
    // console.log("Fetching profile from:", endpoint);
    const response = await axios.get(endpoint, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    
    // Store user ID for future use
    if (response.data && response.data._id) {
      setCurrentUserId(response.data._id);
    }
    
    return response.data;
  } catch (error) {
    // console.error("Error fetching user profile:", error);
    // console.error("Endpoint attempted:", `${API_BASE_URL}/profile`);
    return null;
  }
}
